import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../../utils/api';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const AdmissionApplication = () => {
  useDocumentTitle(
    'School Admission Application | Teaching Hub',
    'Apply online for school admission. Fill out the admission form and track your application status easily.'
  );
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState('');
  const [error, setError] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    gender: '',
    fatherName: '',
    motherName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    previousSchool: '',
    previousStandard: '',
    admissionClass: '',
  });

  const [errors, setErrors] = useState({});

  // Fetch admission form
  useEffect(() => {
    fetchAdmissionForm();
  }, []);

  const fetchAdmissionForm = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/admissions/current/status`);
      setForm(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching form:', error);
      setError('Failed to load admission form. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (form.formFields.dateOfBirth && !formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }

    if (form.formFields.gender && !formData.gender) {
      newErrors.gender = 'Please select a gender';
    }

    if (form.formFields.email && !formData.email) {
      newErrors.email = 'Email is required';
    } else if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (form.formFields.phone && !formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    if (form.formFields.admissionClass && !formData.admissionClass) {
      newErrors.admissionClass = 'Admission class is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Use the academic year of the admission form actually being applied
      // to (already fetched into `form`) rather than recomputing it from
      // today's date — those two can disagree, e.g. when next year's
      // admission window opens before the calendar year rolls over.
      const response = await axios.post(`${API_BASE}/admissions/applications/submit`, {
        academicYear: form.academicYear,
        ...formData,
      });

      setApplicationNumber(response.data.applicationNumber);
      setSubmitted(true);
      setFormData({
        fullName: '',
        dateOfBirth: '',
        gender: '',
        fatherName: '',
        motherName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        previousSchool: '',
        previousStandard: '',
        admissionClass: '',
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to submit application. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTrackApplication = async (e) => {
    e.preventDefault();

    if (!trackingNumber.trim()) {
      setError('Please enter an application number');
      return;
    }

    try {
      setTrackingLoading(true);
      const response = await axios.get(`${API_BASE}/admissions/track/${trackingNumber}`);
      setTrackingResult(response.data);
      setError('');
    } catch (error) {
      console.error('Error tracking application:', error);
      if (error.response?.status === 404) {
        setError('Application not found');
      } else {
        setError('Failed to track application');
      }
      setTrackingResult(null);
    } finally {
      setTrackingLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'bg-blue-100 text-blue-800',
      'under-review': 'bg-yellow-100 text-yellow-800',
      shortlisted: 'bg-purple-100 text-purple-800',
      rejected: 'bg-red-100 text-red-800',
      accepted: 'bg-green-100 text-green-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admission form...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Application Submitted Successfully!
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for applying. Your application has been received.
            </p>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6">
            <p className="text-sm text-gray-600 mb-2">Your Application Number</p>
            <p className="text-2xl font-bold text-green-600 font-mono">
              {applicationNumber}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Use this number to track your application status
            </p>
          </div>

          <button
            onClick={() => {
              setSubmitted(false);
              setApplicationNumber('');
              fetchAdmissionForm();
            }}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  if (!form || form.status === 'not-found') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              No Admission Form Available
            </h2>
            <p className="text-gray-600">
              The admission form for the current academic year is not yet released. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!form.isAccepting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {form.status === 'not-released' && 'Admissions Not Yet Open'}
              {form.status === 'closed' && 'Admission Closed'}
              {form.status === 'coming-soon' && 'Coming Soon'}
              {form.status === 'full' && 'Applications Full'}
            </h2>
            <p className="text-gray-600 mb-4">
              {form.status === 'not-released' &&
                'The admission form for this academic year has not been released yet. Please check back later.'}
              {form.status === 'closed' &&
                'The admission form has been closed. Applications are no longer being accepted.'}
              {form.status === 'coming-soon' &&
                `Application window opens on ${new Date(form.startDate).toLocaleDateString()}`}
              {form.status === 'full' &&
                'The maximum number of applications has been reached.'}
            </p>
            {form.status === 'coming-soon' && (
              <p className="text-sm text-indigo-600 font-semibold">
                📅 {Math.ceil((new Date(form.startDate) - new Date()) / (1000 * 60 * 60 * 24))} days to go
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Online Admission Application
          </h1>
          <p className="text-gray-600 mb-4">
            Academic Year: <span className="font-semibold">{form.academicYear}</span>
          </p>
          {form.description && (
            <p className="text-gray-700 max-w-2xl mx-auto mb-4">{form.description}</p>
          )}
          {form.daysRemaining > 0 && (
            <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
              ⏰ {form.daysRemaining} days remaining
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmitApplication} className="space-y-6">
                {/* Full Name - Always required */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter student's full name"
                  />
                  {errors.fullName && (
                    <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                  )}
                </div>

                {/* Conditional Fields */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Date of Birth */}
                  {form.formFields.dateOfBirth && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.dateOfBirth && (
                        <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>
                      )}
                    </div>
                  )}

                  {/* Gender */}
                  {form.formFields.gender && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Gender <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          errors.gender ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Others">Others</option>
                      </select>
                      {errors.gender && (
                        <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Parent Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  {form.formFields.fatherName && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Father's Name
                      </label>
                      <input
                        type="text"
                        name="fatherName"
                        value={formData.fatherName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Father's full name"
                      />
                    </div>
                  )}

                  {form.formFields.motherName && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mother's Name
                      </label>
                      <input
                        type="text"
                        name="motherName"
                        value={formData.motherName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Mother's full name"
                      />
                    </div>
                  )}
                </div>

                {/* Contact Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  {form.formFields.email && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="parent@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                  )}

                  {form.formFields.phone && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="10-digit phone number"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Address Details */}
                {form.formFields.address && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      rows="3"
                      placeholder="Complete address"
                    ></textarea>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  {form.formFields.city && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="City"
                      />
                    </div>
                  )}

                  {form.formFields.state && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="State"
                      />
                    </div>
                  )}
                </div>

                {form.formFields.pincode && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Pin Code
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="6-digit PIN code"
                    />
                  </div>
                )}

                {/* Previous School Details */}
                {form.formFields.previousSchool && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Previous School
                    </label>
                    <input
                      type="text"
                      name="previousSchool"
                      value={formData.previousSchool}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Previous school name"
                    />
                  </div>
                )}

                {form.formFields.previousStandard && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Previous Standard/Grade
                    </label>
                    <input
                      type="text"
                      name="previousStandard"
                      value={formData.previousStandard}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., Class 4, Grade 5"
                    />
                  </div>
                )}

                {/* Admission Class */}
                {form.formFields.admissionClass && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Applying for Class/Grade <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="admissionClass"
                      value={formData.admissionClass}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        errors.admissionClass ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Class 5, Grade 6"
                    />
                    {errors.admissionClass && (
                      <p className="text-red-500 text-sm mt-1">{errors.admissionClass}</p>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition mt-8"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Form Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Form Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-600">Academic Year</p>
                  <p className="font-semibold text-gray-800">{form.academicYear}</p>
                </div>
                <div>
                  <p className="text-gray-600">Form Status</p>
                  <p className="font-semibold text-green-600">🟢 Open</p>
                </div>
                <div>
                  <p className="text-gray-600">Application Deadline</p>
                  <p className="font-semibold text-gray-800">
                    {new Date(form.endDate).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Applications Received</p>
                  <p className="font-semibold text-gray-800">
                    {form.currentApplicationCount}
                    {form.maximumApplications !== -1 && (
                      <span> / {form.maximumApplications}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Track Application */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">🔍 Track Application</h3>
              <form onSubmit={handleTrackApplication} className="space-y-3">
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter Application Number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
                <button
                  type="submit"
                  disabled={trackingLoading}
                  className="w-full bg-indigo-600 text-white py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 text-sm transition"
                >
                  {trackingLoading ? 'Tracking...' : 'Track'}
                </button>
              </form>

              {trackingResult && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-gray-600">Application No</p>
                      <p className="font-semibold text-gray-800">
                        {trackingResult.applicationNumber}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Applicant Name</p>
                      <p className="font-semibold text-gray-800">{trackingResult.fullName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Status</p>
                      <p className={`font-semibold px-2 py-1 rounded text-xs inline-block ${getStatusColor(trackingResult.status)}`}>
                        {trackingResult.status.charAt(0).toUpperCase() +
                          trackingResult.status.slice(1)}
                      </p>
                    </div>
                    {trackingResult.remarks && (
                      <div>
                        <p className="text-gray-600">Remarks</p>
                        <p className="text-gray-700">{trackingResult.remarks}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-600">Submitted Date</p>
                      <p className="font-semibold text-gray-800">
                        {new Date(trackingResult.submittedAt).toLocaleDateString(
                          'en-IN'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-4">📝 Instructions</h3>
              <ul className="space-y-2 text-sm text-blue-900">
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>Fill all required fields marked with *</span>
                </li>
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>Ensure email and phone are correct</span>
                </li>
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>Save your application number for future reference</span>
                </li>
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>You can track your application status anytime</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionApplication;
